import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ncrApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";

export default function NCRViewPage() {
  const { id } = useParams<{ id: string }>();
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [remarks, setRemarks] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { data: ncr, loading, error, reload } = useAsyncData(
    () => ncrApi.get(Number(id)),
    [id],
  );

  const { mutate: doResolve, loading: resolving, error: resolveErr } = useMutation(() =>
    ncrApi.resolve(Number(id), correctiveAction || undefined).then(() => { setCorrectiveAction(""); reload(); }),
  );

  const { mutate: doClose, loading: closing, error: closeErr } = useMutation(() =>
    ncrApi.close(Number(id), remarks || undefined).then(() => { setRemarks(""); reload(); }),
  );

  async function downloadPdf() {
    if (!ncr) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.ncr(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ncr-${ncr.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!ncr) return null;

  const actionErr = resolveErr || closeErr;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/ncrs" className="text-sm text-emerald-700 hover:underline">&larr; Back to NCRs</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-06" title="Non-Conformance Report" serialNo={ncr.serialNo} date={ncr.date}>
        <ProcSection title="NCR Details">
          <ProcGrid>
            <ProcField label="LPO" value={ncr.lpoSerialNo ?? String(ncr.lpoId)} />
            <ProcField label="Delivery Note No" value={ncr.deliveryNoteNo} />
            <ProcField label="Department" value={ncr.departmentName ?? String(ncr.departmentId)} />
            <ProcField label="Supplier" value={ncr.supplierName} />
            <ProcField label="Status" value={<ProcStatusBadge value={ncr.status} />} />
            <ProcField label="Raised By" value={ncr.raisedByUsername ?? (ncr.raisedByUserId ? String(ncr.raisedByUserId) : "—")} />
            <ProcField label="Authorized By" value={ncr.authorizedByUsername ?? (ncr.authorizedByUserId ? String(ncr.authorizedByUserId) : "—")} />
            <ProcField label="Description" value={ncr.description} span2 />
            <ProcField label="Corrective Action" value={ncr.correctiveAction} span2 />
          </ProcGrid>
        </ProcSection>

        {(ncr.status === "OPEN" || ncr.status === "RESOLVED") && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            {ncr.status === "OPEN" && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Corrective Action (optional)
                  </label>
                  <textarea
                    value={correctiveAction}
                    onChange={e => setCorrectiveAction(e.target.value)}
                    rows={2}
                    className="w-full rounded-xl border px-3 py-2 text-sm"
                    placeholder="Describe corrective action taken…"
                  />
                </div>
                <button type="button" onClick={() => doResolve()} disabled={resolving}
                  className="rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60">
                  {resolving ? "Resolving…" : "Mark as Resolved"}
                </button>
              </div>
            )}
            {ncr.status === "RESOLVED" && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Closing Remarks (optional)
                  </label>
                  <textarea
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    rows={2}
                    className="w-full rounded-xl border px-3 py-2 text-sm"
                    placeholder="Any closing remarks…"
                  />
                </div>
                <button type="button" onClick={() => doClose()} disabled={closing}
                  className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
                  {closing ? "Closing…" : "Close NCR"}
                </button>
              </div>
            )}
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
